import { Component, inject, Input, OnDestroy, OnInit, ChangeDetectorRef, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { GenericSearchSelectComponent } from '../generic-search-select/generic-search-select.component';
import { GenericFormComponent } from '../generic-form/generic-form.component';
import { GeoLocationService } from '../../../features/private/services/geo-location.service';
import { PermissionService } from '../../../core/services/permission.service';
import { SystemPower } from '../../../shared/model/permissions';
import { AddressDto, GeoLocation } from '../../model/geoLocation';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-location-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    GenericSearchSelectComponent,
    GenericFormComponent,
  ],
  templateUrl: './location-form.component.html',
  styleUrl: './location-form.component.css',
})
export class LocationFormComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup;
  @Input() addNewLocation: boolean = false;
  @Input() required: boolean = false;
  @Input() organizationMode: boolean = false;

  private locationService: GeoLocationService = inject(GeoLocationService);
  private permissionService: PermissionService = inject(PermissionService);
  private subs: Subscription = new Subscription();
  private isInternalUpdate: boolean = false;
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  protected searchControl = new FormControl<GeoLocation | null>(null);

  protected _myLocations = signal<Array<GeoLocation>>([]);
  public get myLocations(): Array<GeoLocation> {
    return this._myLocations();
  }
  public set myLocations(value: Array<GeoLocation>) {
    this._myLocations.set(value);
  }

  protected _selectedLocation = signal<GeoLocation | null>(null);
  public get selectedLocation(): GeoLocation | null {
    return this._selectedLocation();
  }
  public set selectedLocation(value: GeoLocation | null) {
    this._selectedLocation.set(value);
  }

  protected _manualLocationMode = signal<boolean>(false);
  public get manualLocationMode(): boolean {
    return this._manualLocationMode();
  }
  public set manualLocationMode(value: boolean) {
    this._manualLocationMode.set(value);
  }

  constructor() {
    this.displayMineLocation = this.displayMineLocation.bind(this);
  }

  ngOnInit(): void {
    if (this.addNewLocation) {
      this.manualLocationMode = true;
    }

    this.syncSelectionFromLocationGroup();
    this.loadMyLocations();

    this.subs.add(
      this.searchControl.valueChanges.subscribe((selected) => {
        if (!selected && !this.manualLocationMode && this.selectedLocation) {
          this.clearLocation();
        }
      }),
    );

    if (this.locationGroup) {
      this.subs.add(
        this.locationGroup.valueChanges.subscribe(() => {
          if (this.isInternalUpdate) {
            return;
          }
          if (!this.manualLocationMode && !this.selectedLocation) {
            this.syncSelectionFromLocationGroup();
          }
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public get locationGroup(): FormGroup {
    return this.parentForm?.get('location') as FormGroup;
  }

  public get showRequiredLocationError(): boolean {
    if (!this.required || !this.locationGroup) {
      return false;
    }

    return (
      this.locationGroup.hasError('locationRequired') &&
      (this.locationGroup.touched || this.locationGroup.dirty)
    );
  }

  public displayMineLocation(location: GeoLocation): string {
    if (!location) {
      return '';
    }
    const formalName = (location.formalName || '').trim();
    const address =
      this.locationService.addressToLabel(location.address) ||
      [location.street, location.houseNumber, location.city].filter(Boolean).join(', ');

    if (formalName && address && formalName.toLowerCase() !== address.toLowerCase()) {
      return `${formalName} (${address})`;
    }
    return formalName || address || location.displayName || '';
  }

  public get selectedLocationTitle(): string {
    if (!this.selectedLocation) {
      return '';
    }
    return (
      (this.selectedLocation.formalName || '').trim() ||
      this.locationService.addressToLabel(this.selectedLocation.address) ||
      this.selectedLocation.displayName ||
      'Luogo selezionato'
    );
  }

  public get selectedLocationAddress(): string {
    if (!this.selectedLocation) {
      return '';
    }
    const address = this.locationService.addressToLabel(this.selectedLocation.address);
    const line = [
      this.selectedLocation.street,
      this.selectedLocation.houseNumber,
      this.selectedLocation.postcode,
      this.selectedLocation.city,
      this.selectedLocation.province,
      this.selectedLocation.country,
    ]
      .filter(Boolean)
      .join(', ');
    return address || line || '';
  }

  public selectSavedLocationMode(): void {
    this.manualLocationMode = false;
    if (this.selectedLocation) {
      this.locationSelected(this.selectedLocation);
    } else {
      this.syncSelectionFromLocationGroup();
    }
  }

  public selectManualLocationMode(): void {
    this.manualLocationMode = true;
    if (this.locationGroup) {
      this.isInternalUpdate = true;
      this.locationGroup.get('id')?.setValue('');
      this.locationGroup.get('country')?.setValue('Italia');
      this.isInternalUpdate = false;
      this.applyManualLocationData();
    }
  }

  public deleteSavedLocation(location: GeoLocation): void {
    if (!location || !location.id) {
      return;
    }

    if (confirm('Sei sicuro di voler eliminare questo luogo salvato?')) {
      this.subs.add(
        this.locationService.deleteLocation(location.id).subscribe({
          next: (response) => {
            if (response.success) {
              this.myLocations = this.myLocations.filter((loc) => loc.id !== location.id);
              if (this.selectedLocation?.id === location.id) {
                this.clearLocation();
              }
            }
          },
        }),
      );
    }
  }

  public onManualLocationChange(): void {
    if (!this.manualLocationMode) {
      return;
    }
    this.applyManualLocationData();
  }

  public locationSelected(location: GeoLocation | null): void {
    if (!location) {
      this.clearLocation();
      return;
    }

    this.selectedLocation = location;
    this.manualLocationMode = false;

    const normalized =
      (this.locationService.normalizeLocation(location) as GeoLocation) || location;
    const addressLabel = this.locationService.addressToLabel(normalized.address);
    const formalName =
      normalized.formalName || normalized.displayName || normalized.rawInput || '';
    const street = normalized.street || '';
    const houseNumber = normalized.houseNumber || '';
    const city = normalized.city || normalized.town || normalized.village || '';
    const province = normalized.province || '';
    const postcode = normalized.postcode || '';
    const country = normalized.country || 'Italia';
    const lat = normalized.lat || (normalized.latitude != null ? `${normalized.latitude}` : '');
    const lon = normalized.lon || (normalized.longitude != null ? `${normalized.longitude}` : '');

    const line = [street, houseNumber].filter(Boolean).join(' ').trim();
    const formattedAddress =
      [line, postcode, city, province, country].filter(Boolean).join(', ') || addressLabel;

    queueMicrotask(() => {
      this.isInternalUpdate = true;
      this.locationGroup.patchValue({
        id: normalized.id,
        formalName,
        displayName: formattedAddress || formalName,
        address: normalized.address,
        city,
        country,
        town: normalized.town || '',
        village: normalized.village || '',
        neighbourhood: normalized.neighbourhood || '',
        residential: normalized.residential || '',
        postcode,
        street,
        houseNumber,
        province,
        rawInput: formattedAddress || formalName,
        latitude: normalized.latitude ?? (lat ? Number(lat) : undefined),
        longitude: normalized.longitude ?? (lon ? Number(lon) : undefined),
        lat,
        lon,
      });
      this.isInternalUpdate = false;

      this.locationGroup.markAsDirty();
      this.locationGroup.updateValueAndValidity();
      this.parentForm?.updateValueAndValidity();
      this.cdr.markForCheck();
    });
  }

  public clearLocation(): void {
    this.selectedLocation = null;
    this.searchControl.setValue(null, { emitEvent: false });
    if (!this.locationGroup) {
      return;
    }

    this.isInternalUpdate = true;
    this.locationGroup.patchValue({
      id: '',
      formalName: '',
      displayName: '',
      address: null,
      city: '',
      country: 'Italia',
      town: '',
      village: '',
      neighbourhood: '',
      residential: '',
      postcode: '',
      street: '',
      houseNumber: '',
      province: '',
      rawInput: '',
      latitude: undefined,
      longitude: undefined,
      lat: '',
      lon: '',
    });
    this.isInternalUpdate = false;

    this.locationGroup.markAsDirty();
    this.locationGroup.updateValueAndValidity();
    this.parentForm?.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  private loadMyLocations(): void {
    const locations$ = this.permissionService.hasSystemPower(SystemPower.ADMINISTRATOR)
      ? this.locationService.getAllLocations()
      : this.locationService.getMyLocations('');

    this.subs.add(
      locations$.subscribe((response) => {
        this.myLocations = response.success && response.data ? response.data : [];
        if (this.selectedLocation && !this.myLocations.find(loc => loc.id === this.selectedLocation!.id)) {
          this.myLocations = [this.selectedLocation, ...this.myLocations];
        }
        this.syncSelectionFromLocationGroup();
        this.cdr.markForCheck();
      }),
    );
  }

  private syncSelectionFromLocationGroup(): void {
    if (!this.locationGroup) {
      return;
    }

    queueMicrotask(() => {
      const currentId = this.locationGroup.get('id')?.value;
      const currentFormalName = (this.locationGroup.get('formalName')?.value || '')
        .toString()
        .trim();
      const currentStreet = (this.locationGroup.get('street')?.value || '').toString().trim();
      const currentCity = (this.locationGroup.get('city')?.value || '').toString().trim();

      // 1. If an ID is present, find in myLocations
      if (currentId && this.myLocations.length > 0) {
        const match = this.myLocations.find((loc) => String(loc.id) === String(currentId));
        if (match) {
          this.selectedLocation = match;
          this.searchControl.setValue(match);
          this.manualLocationMode = false;
          this.parentForm?.updateValueAndValidity();
          this.cdr.markForCheck();
          return;
        }
      }

      // 2. If formalName matches an existing location
      if (this.myLocations.length > 0 && currentFormalName) {
        const match = this.myLocations.find(
          (loc) =>
            loc.formalName &&
            loc.formalName.trim().toLowerCase() === currentFormalName.toLowerCase(),
        );
        if (match) {
          this.selectedLocation = match;
          this.searchControl.setValue(match);
          this.manualLocationMode = false;
          this.parentForm?.updateValueAndValidity();
          this.cdr.markForCheck();
          return;
        }
      }

      // 3. If there are manual address values without ID match but we have a prefilled location
      const currentDisplayName = (this.locationGroup.get('displayName')?.value || '').toString().trim();
      if (currentId || currentStreet || currentCity || currentFormalName || currentDisplayName) {
        // Create a fallback location object from the form
        const fallbackLoc = this.locationService.normalizeLocation(this.locationGroup.getRawValue()) as GeoLocation;

        // If we don't already have it in myLocations, add it
        if (!this.myLocations.find(l => (fallbackLoc.id && l.id === fallbackLoc.id) || (l.formalName && fallbackLoc.formalName && l.formalName.toLowerCase() === fallbackLoc.formalName.toLowerCase()))) {
          this.myLocations = [fallbackLoc, ...this.myLocations];
        }

        this.selectedLocation = fallbackLoc;
        this.searchControl.setValue(fallbackLoc);
        this.manualLocationMode = false;
        this.parentForm?.updateValueAndValidity();
        this.cdr.markForCheck();
        return;
      }

      // 4. Default state
      if (this.addNewLocation || this.myLocations.length === 0) {
        this.manualLocationMode = true;
      } else {
        this.manualLocationMode = false;
      }

      this.parentForm?.updateValueAndValidity();
      this.cdr.markForCheck();
    });
  }

  private applyManualLocationData(): void {
    const locationGroup = this.locationGroup;
    if (!locationGroup) {
      return;
    }

    const formalName = (locationGroup.get('formalName')?.value || '').toString();
    const street = (locationGroup.get('street')?.value || '').toString();
    const houseNumber = (locationGroup.get('houseNumber')?.value || '').toString();
    const city = (locationGroup.get('city')?.value || '').toString();
    const province = (locationGroup.get('province')?.value || '').toString();
    const country = 'Italia';

    const address: AddressDto = {
      street: street || undefined,
      houseNumber: houseNumber || undefined,
      city: city || undefined,
      province: province || undefined,
      country,
    };

    const streetTrim = street.trim();
    const houseNumberTrim = houseNumber.trim();
    const cityTrim = city.trim();
    const provinceTrim = province.trim();
    const line = [streetTrim, houseNumberTrim].filter(Boolean).join(' ').trim();
    const formattedAddress = [line, cityTrim, provinceTrim, country]
      .filter(Boolean)
      .join(', ');

    this.isInternalUpdate = true;
    locationGroup.patchValue(
      {
        id: '',
        displayName: formattedAddress || formalName,
        formalName,
        address,
        city,
        country,
        town: '',
        village: '',
        neighbourhood: '',
        residential: '',
        postcode: '',
        street,
        houseNumber,
        province,
        rawInput: formattedAddress || formalName,
        latitude: undefined,
        longitude: undefined,
        lat: '',
        lon: '',
      },
      { emitEvent: true },
    );
    this.isInternalUpdate = false;

    locationGroup.markAsDirty();
    locationGroup.updateValueAndValidity();
    this.parentForm?.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  private parseCoordinate(value: string): number | undefined {
    if (!value) {
      return undefined;
    }

    const normalized = value.replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
